import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, LayoutGrid, Search } from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import "../styles/theme.css";
import "../styles/ProfilesManager.css";
import "../styles/ExampleTrainingPrompts.css";
import EXAMPLE_PROMPTS from "./help/examplePromptsData";
import { industryLabels, subIndustryLabels } from "../utils/categoryLabels";

export default function ExampleTrainingPrompts() {
  const { t } = useTranslation("exampleTrainingPrompts");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedSubIndustry, setSelectedSubIndustry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);

  const industries = useMemo(() => Object.keys(EXAMPLE_PROMPTS), []);
  const subIndustries = useMemo(
    () => (selectedIndustry ? Object.keys(EXAMPLE_PROMPTS[selectedIndustry]) : []),
    [selectedIndustry]
  );
  const prompts = useMemo(() => {
    if (!selectedIndustry || !selectedSubIndustry) return [];
    return EXAMPLE_PROMPTS[selectedIndustry][selectedSubIndustry] || [];
  }, [selectedIndustry, selectedSubIndustry]);

  useEffect(() => {
    setSearchQuery("");
  }, [selectedIndustry, selectedSubIndustry]);

  const filteredWithIndex = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return prompts
      .map((text, idx) => ({ text, idx }))
      .filter(({ text }) => !q || text.toLowerCase().includes(q));
  }, [prompts, searchQuery]);

  const selectionReady = Boolean(selectedIndustry && selectedSubIndustry);

  const copyText = useCallback(async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
    } catch {
      setCopiedKey(null);
    }
  }, []);

  useEffect(() => {
    if (!copiedKey) return undefined;
    const timer = setTimeout(() => setCopiedKey(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedKey]);

  const handleCopyAll = useCallback(() => {
    if (!filteredWithIndex.length) return;
    const blob = filteredWithIndex.map(({ text }) => text).join("\n");
    copyText(blob, "all");
  }, [filteredWithIndex, copyText]);

  return (
    <div className="theme-whbl">
      <Header />
      <div className="main-container" style={{ minHeight: "100vh" }}>
        <div style={{ height: "100vh" }}>
          <Sidebar />
        </div>
        <div className="main-content" style={{ flex: 1, padding: "20px", minHeight: "100vh" }}>
          <div className="hetp-page">
            <header className="hetp-hero">
              <h1 className="hetp-hero-title">{t("title")}</h1>
              <p className="hetp-hero-desc">{t("description")}</p>
            </header>

            <section className="hetp-filters" aria-label={t("filtersAriaLabel")}>
              <div className="hetp-filters-grid">
                <div className="hetp-select-wrap">
                  <label className="hetp-label" htmlFor="etp-industry-select">
                    {t("industry")}
                  </label>
                  <select
                    id="etp-industry-select"
                    className="form-select"
                    value={selectedIndustry}
                    onChange={(e) => {
                      setSelectedIndustry(e.target.value);
                      setSelectedSubIndustry("");
                    }}
                  >
                    <option value="">{t("chooseIndustry")}</option>
                    {industries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industryLabels[industry] || industry}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hetp-select-wrap">
                  <label className="hetp-label" htmlFor="etp-sub-industry-select">
                    {t("subIndustry")}
                  </label>
                  <select
                    id="etp-sub-industry-select"
                    className="form-select"
                    value={selectedSubIndustry}
                    onChange={(e) => setSelectedSubIndustry(e.target.value)}
                    disabled={!selectedIndustry}
                  >
                    <option value="">{t("chooseSubIndustry")}</option>
                    {subIndustries.map((subIndustry) => (
                      <option key={subIndustry} value={subIndustry}>
                        {subIndustryLabels[selectedIndustry]?.[subIndustry] || subIndustry}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hetp-search-wrap">
                  <label className="hetp-label" htmlFor="etp-search">
                    {t("searchPrompts")}
                  </label>
                  <div className="hetp-search-inner">
                    <Search className="hetp-search-icon" size={18} strokeWidth={2} aria-hidden />
                    <input
                      id="etp-search"
                      type="search"
                      className="hetp-search-input"
                      placeholder={
                        selectionReady ? t("searchPlaceholder") : t("searchPlaceholderDisabled")
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={!selectionReady}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </section>

            {!selectionReady ? (
              <div className="hetp-empty" role="status">
                <LayoutGrid
                  size={40}
                  strokeWidth={1.25}
                  style={{ margin: "0 auto 1rem", opacity: 0.35 }}
                  aria-hidden
                />
                <p className="hetp-empty-title">{t("chooseSelection")}</p>
              </div>
            ) : (
              <>
                <div className="hetp-toolbar">
                  <p className="hetp-count">
                    {searchQuery.trim()
                      ? t("showingFiltered", {
                          shown: filteredWithIndex.length,
                          total: prompts.length,
                        })
                      : t("showing", { count: filteredWithIndex.length })}
                  </p>
                  <button
                    type="button"
                    className="hetp-copy-all"
                    onClick={handleCopyAll}
                    disabled={!filteredWithIndex.length}
                    title={t("copyAllTitle")}
                  >
                    {copiedKey === "all" ? (
                      <>
                        <Check size={16} strokeWidth={2.5} aria-hidden />
                        {t("copiedAll")}
                      </>
                    ) : (
                      <>
                        <Copy size={16} strokeWidth={2} aria-hidden />
                        {t("copyAll")}
                      </>
                    )}
                  </button>
                </div>

                {filteredWithIndex.length === 0 ? (
                  <div className="hetp-empty" role="status">
                    <Search
                      size={36}
                      strokeWidth={1.25}
                      style={{ margin: "0 auto 1rem", opacity: 0.35 }}
                      aria-hidden
                    />
                    <p className="hetp-empty-title">{t("noMatches")}</p>
                    <p className="hetp-empty-desc">
                      {t("noMatchesDesc", { query: searchQuery.trim() })}
                    </p>
                    <p className="hetp-empty-hint">
                      <button
                        type="button"
                        className="hetp-copy-btn"
                        onClick={() => setSearchQuery("")}
                        style={{ marginTop: "0.25rem" }}
                      >
                        {t("clearSearch")}
                      </button>
                    </p>
                  </div>
                ) : (
                  <div className="hetp-grid">
                    {filteredWithIndex.map(({ text, idx }) => {
                      const displayNum = idx + 1;
                      const key = `${selectedSubIndustry}-${idx}`;
                      const isCopied = copiedKey === key;
                      return (
                        <article key={key} className="hetp-card">
                          <div className="hetp-card-top">
                            <span className="hetp-card-num" aria-hidden>
                              {displayNum}
                            </span>
                            <p className="hetp-card-text">{text}</p>
                            <button
                              type="button"
                              className={`hetp-copy-btn${isCopied ? " hetp-copy-btn--done" : ""}`}
                              onClick={() => copyText(text, key)}
                              aria-label={
                                isCopied ? t("copied") : t("copyPrompt", { number: displayNum })
                              }
                              title={isCopied ? t("copied") : t("copyPromptTitle")}
                            >
                              {isCopied ? (
                                <Check size={14} strokeWidth={2.5} aria-hidden />
                              ) : (
                                <Copy size={14} strokeWidth={2} aria-hidden />
                              )}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
